import { readStorageItem, writeStorageItem } from "./storage";
export const THEME_PREFERENCES = ["light", "dark", "system"] as const;

export type ShellThemePreference = (typeof THEME_PREFERENCES)[number];
export type ResolvedShellTheme = Exclude<ShellThemePreference, "system">;

const STORAGE_KEY = "shell.theme";
const SYSTEM_THEME_MEDIA_QUERY = "(prefers-color-scheme: dark)";

type StorageLike = Pick<Storage, "getItem" | "setItem">;

type MediaQueryListLike = Pick<MediaQueryList, "matches">;

type DetectionSources = {
  storage?: StorageLike;
  matchMedia?: (query: string) => MediaQueryListLike | undefined;
  documentPreference?: string | null | undefined;
};

const getBrowserStorage = () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const getDocumentPreference = () => {
  if (typeof document === "undefined") {
    return undefined;
  }

  return document.documentElement.getAttribute("data-theme-preference");
};

const getBrowserMatchMedia = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return undefined;
  }

  return (query: string) => window.matchMedia(query);
};

export const resolveThemePreference = (
  candidate?: string | null,
): ShellThemePreference | undefined => {
  if (!candidate) {
    return undefined;
  }

  const normalized = candidate.trim().toLowerCase();

  return THEME_PREFERENCES.includes(normalized as ShellThemePreference)
    ? (normalized as ShellThemePreference)
    : undefined;
};

export const resolveSystemTheme = (
  sources: Pick<DetectionSources, "matchMedia"> = {},
): ResolvedShellTheme => {
  const matchMedia = sources.matchMedia ?? getBrowserMatchMedia();

  try {
    return matchMedia?.(SYSTEM_THEME_MEDIA_QUERY)?.matches ? "dark" : "light";
  } catch {
    return "light";
  }
};

export const resolveEffectiveTheme = (
  preference: ShellThemePreference,
  systemTheme = resolveSystemTheme(),
): ResolvedShellTheme =>
  preference === "system" ? systemTheme : preference;

export const detectInitialThemePreference = (
  sources: DetectionSources = {},
): ShellThemePreference => {
  const storage = sources.storage ?? getBrowserStorage();
  const storedCandidate = (() => {
    if (!storage) {
      return readStorageItem(STORAGE_KEY);
    }

    try {
      return storage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  })();
  const storedPreference = resolveThemePreference(
    storedCandidate,
  );

  if (storedPreference) {
    return storedPreference;
  }

  const documentPreference = resolveThemePreference(
    sources.documentPreference ?? getDocumentPreference(),
  );

  if (documentPreference) {
    return documentPreference;
  }

  return "system";
};

export const persistThemePreference = (
  preference: ShellThemePreference,
  sources: { storage?: StorageLike } = {},
) => {
  const storage = sources.storage ?? getBrowserStorage();

  if (!storage && !sources.storage) {
    writeStorageItem(STORAGE_KEY, preference);
    return;
  }

  if (!storage) {
    return;
  }

  try {
    storage.setItem(STORAGE_KEY, preference);
  } catch {
    // Ignore persistence failures for private browsing or restricted storage.
  }
};

export const getThemeStorageKey = () => STORAGE_KEY;
export const getSystemThemeMediaQuery = () => SYSTEM_THEME_MEDIA_QUERY;
